import { useEffect } from 'react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { useBlocker } from 'react-router';

/**
 * Initializes the editor styles and the Business Manager preview connection.
 */
export function PageDesignerInit({ usid }: { usid?: string } = {}) {
    const { isDesignMode, isPreviewMode } = usePageDesignerMode();

    // Prevent navigation in React Router when in Page Designer design mode
    // so that clicks on links don't cause route changes during editing.
    useBlocker(() => isDesignMode);

    // Dynamically import the Page Designer global styles only when in design mode.
    useEffect(() => {
        if (isDesignMode) {
            void import('@salesforce/storefront-next-runtime/design/styles.css');
        }
    }, [isDesignMode]);

    useEffect(() => {
        if (!isPreviewMode || isDesignMode || window.parent === window) return;

        // The runtime connects its messaging client only in EDIT mode. BM also waits
        // for ClientReady in PREVIEW mode before revealing the storefront iframe.
        let disposed = false;
        let disconnect: (() => void) | undefined;

        void import('@salesforce/storefront-next-runtime/design/messaging').then(({ createClientApi }) => {
            if (disposed) return;

            const client = createClientApi({
                id: 'storefront-next',
                emitter: {
                    postMessage: (message) => window.parent.postMessage(message, '*'),
                    addEventListener: (handler) => {
                        const listener = (event: MessageEvent) => {
                            if (event.source === window.parent) handler(event.data);
                        };
                        window.addEventListener('message', listener);
                        return () => window.removeEventListener('message', listener);
                    },
                },
            });

            disconnect = () => client.disconnect();
            client.connect({ usid, onHostDisconnected: (reconnect) => reconnect() });
        });

        return () => {
            disposed = true;
            disconnect?.();
        };
    }, [isDesignMode, isPreviewMode, usid]);

    return <></>;
}
